import { Button } from "@/components/ui/button";
import {
  getImpersonationSession,
  IMPERSONATION_CHANGED_EVENT,
  stopImpersonation,
  type ImpersonationSession,
} from "@/lib/impersonation";
import { ShieldAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export default function ImpersonationBanner() {
  const { t } = useTranslation("crm");
  const [session, setSession] = useState<ImpersonationSession | null>(() =>
    getImpersonationSession(),
  );
  const [stopping, setStopping] = useState(false);

  useEffect(() => {
    const refresh = () => setSession(getImpersonationSession());
    window.addEventListener(IMPERSONATION_CHANGED_EVENT, refresh);
    return () =>
      window.removeEventListener(IMPERSONATION_CHANGED_EVENT, refresh);
  }, []);

  if (!session) return null;

  const stop = async () => {
    setStopping(true);
    try {
      await stopImpersonation();
    } finally {
      // The local simulation is cleared even when the audit endpoint is
      // temporarily unreachable, so the owner can never remain trapped in a
      // restricted role.
      window.location.assign("/crm/users");
    }
  };

  return (
    <div
      role="status"
      className="sticky top-0 z-[100] border-b border-amber-400/50 bg-amber-300 px-4 py-2 text-amber-950 shadow-sm"
    >
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2 text-sm font-semibold">
          <ShieldAlert className="size-4 shrink-0" />
          <span>
            {t("impersonation.active", {
              name: session.target.name,
              role: t(
                `workspace.role.${session.target.legacyRole || session.target.role}`,
                session.target.legacyRole || session.target.role,
              ),
            })}
          </span>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="border-amber-700 bg-amber-50 text-amber-950 hover:bg-white"
          disabled={stopping}
          onClick={() => void stop()}
        >
          {stopping ? t("impersonation.stopping") : t("impersonation.stop")}
        </Button>
      </div>
    </div>
  );
}
